import type { AssetProfile, SecondOrderDomainKey } from '@akem/shared';
import {
    Alert,
    Box,
    Breadcrumbs,
    Card,
    CardActionArea,
    CardContent,
    CardHeader,
    Chip,
    Container,
    Grid,
    Link,
    Skeleton,
    Stack,
    Typography
} from '@mui/material';
import React from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import type { NewsArticle } from '../../services/perplexity.js';
import { newsApi } from '../../services/perplexity.js';
import { getProfile } from '../../services/profile.js';
import { WidgetHost } from '../dashboard/WidgetHost.js';
import { StorylineChat } from '../intelligence/components/StorylineChat.tsx';
import { IntelligenceGrid } from '../market/components/IntelligenceGrid.js';
import { ProfileExposurePanel } from './components/ProfileExposurePanel.js';

const SECOND_ORDER_DOMAINS: SecondOrderDomainKey[] = [
  'marketTrends',
  'politics',
  'regulatory',
  'mna',
  'logistics',
  'customers',
  'suppliers',
  'themes',
];

const DOMAIN_LABELS: Record<SecondOrderDomainKey, string> = {
  marketTrends: 'Market Trends',
  politics: 'Politics',
  regulatory: 'Regulatory',
  mna: 'M&A',
  logistics: 'Logistics',
  customers: 'Customers',
  suppliers: 'Suppliers',
  themes: 'Themes',
};

const NEWS_DISPLAY_LIMIT = 6;

const formatRelativeTime = (timestamp?: string): string => {
  if (!timestamp) return '';
  const parsed = new Date(timestamp);
  if (!Number.isFinite(parsed.getTime())) return '';
  const diffMinutes = Math.floor((Date.now() - parsed.getTime()) / 60000);
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

export const ProfileDashboardPage: React.FC = () => {
  const { id = '' } = useParams();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [profile, setProfile] = React.useState<AssetProfile | null>(null);
  const [ticker, setTicker] = React.useState<string | null>(null);
  const [marketNews, setMarketNews] = React.useState<NewsArticle[] | null>(null);
  const [newsError, setNewsError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getProfile({ idOrTicker: id, useMock: false }).catch(() => null);
        if (cancelled) return;
        if (data) {
          setProfile(data);
          setTicker(data.ticker ? data.ticker.toUpperCase() : null);
        } else {
          setProfile(null);
          setError('Profile not found');
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[ProfileDashboard] failed to load profile', err);
          setProfile(null);
          setError(err instanceof Error ? err.message : 'Failed to load profile');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Load market news for bottom section
  React.useEffect(() => {
    let cancelled = false;
    const symbol = ticker || profile?.ticker;

    (async () => {
      try {
        setNewsError(null);
        if (symbol) {
          const articles = await newsApi.getStockNews(symbol, { limit: 12 });
          if (!cancelled) setMarketNews(articles);
        } else {
          const data = await newsApi.getMarketNews({ limit: 12 });
          if (!cancelled) setMarketNews(data.articles || []);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[ProfileDashboard] failed to load news', err);
          setMarketNews([]);
          setNewsError('Unable to load news');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [ticker, profile?.ticker]);

  const handleHeaderOutputs = React.useCallback((outputs: Record<string, any>) => {
    const emittedTicker = outputs.ticker;
    if (typeof emittedTicker === 'string' && emittedTicker.trim().length > 0) {
      setTicker(emittedTicker.toUpperCase());
    }
  }, []);

  const effectiveTicker = ticker ?? profile?.ticker ?? 'AAPL';
  const profileLabel = profile?.name
    ? `${profile.name}${effectiveTicker ? ` (${effectiveTicker})` : ''}`
    : (profile?.id || id);

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: 'background.default' }}>
      <Container maxWidth="xl" sx={{ py: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Breadcrumb back to Market Canvas */}
        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          sx={{
            '& .MuiBreadcrumbs-separator': { color: 'text.secondary' },
            '& .MuiBreadcrumbs-ol': { alignItems: 'center' },
            color: 'text.secondary',
            fontSize: '0.875rem',
          }}
        >
          <Link
            component={RouterLink}
            underline="hover"
            color="inherit"
            to="/market"
            sx={{ display: 'inline-flex', alignItems: 'center', fontSize: '0.875rem' }}
          >
            Market Dashboard
          </Link>
          <Typography color="text.primary" variant="body2" fontWeight="500">
            {profileLabel}
          </Typography>
        </Breadcrumbs>

        {!loading && !error && profile && (
          <Stack spacing={4}>
            <WidgetHost
              type="profileHeader"
              config={{ profileId: profile.id }}
              inputs={{ profile }}
              onOutputsChange={handleHeaderOutputs}
              disableCard
            />
            
            <Box>
              <Typography 
                variant="h5" 
                fontWeight="600" 
                sx={{ 
                  mb: 2,
                  textShadow: '0 0 40px rgba(184, 137, 255, 0.6), 0 0 80px rgba(184, 137, 255, 0.4)',
                }}
              >
                Akem Intelligence
              </Typography>
              
              <IntelligenceGrid 
                tiles={SECOND_ORDER_DOMAINS.map((domain) => ({
                  id: domain,
                  title: DOMAIN_LABELS[domain],
                  profileIds: [], // Not used when profileId is present
                  profileId: profile.id,
                  viewMode: 'portfolio', // Or a new mode if needed, but 'portfolio' with profileId works
                }))} 
              />

              <Box sx={{ mt: 3 }}>
                <StorylineChat profileId={profile.id} ticker={effectiveTicker} />
              </Box>
            </Box>

            <Box>
              <Typography variant="h5" fontWeight="600" sx={{ mb: 2 }}>
                Deep Research
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ height: 400, display: 'flex', flexDirection: 'column' }}>
                    <CardHeader 
                      title="Customer Segments" 
                      titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} 
                      sx={{ pb: 0, pt: 2, px: 2 }}
                    />
                    <CardContent sx={{ p: 0, flex: 1, overflow: 'hidden' }}>
                      <WidgetHost
                        type="customerSegments"
                        config={{ title: 'Customer Segments' }}
                        inputs={{ profile }}
                        disableCard
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ height: 400, display: 'flex', flexDirection: 'column' }}>
                    <CardHeader 
                      title="Suppliers" 
                      titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} 
                      sx={{ pb: 0, pt: 2, px: 2 }}
                    />
                    <CardContent sx={{ p: 0, flex: 1, overflow: 'hidden' }}>
                      <WidgetHost
                        type="supplyChain"
                        config={{ title: 'Supply Chain' }}
                        inputs={{ profile }}
                        disableCard
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ height: 400, display: 'flex', flexDirection: 'column' }}>
                    <CardHeader 
                      title="Regulatory" 
                      titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} 
                      sx={{ pb: 0, pt: 2, px: 2 }}
                    />
                    <CardContent sx={{ p: 0, flex: 1, overflow: 'hidden' }}>
                      <WidgetHost
                        type="legislation"
                        config={{ title: 'Legislation' }}
                        inputs={{ profile }}
                        disableCard
                      />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        )}

        <Stack spacing={4}>
          <Typography variant="h5" fontWeight="600">
            {profile?.name ? `${profile.name} Overview` : 'Profile Overview'}
          </Typography>
          {loading && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Skeleton variant="rounded" height={160} />
              </Grid>
              <Grid item xs={12} md={8}>
                <Skeleton variant="rounded" height={320} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Skeleton variant="rounded" height={320} />
              </Grid>
              <Grid item xs={12}>
                <Skeleton variant="rounded" height={240} />
              </Grid>
              <Grid item xs={12}>
                <Skeleton variant="rounded" height={240} />
              </Grid>
            </Grid>
          )}
          {!loading && error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}
          {!loading && !error && profile && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ height: '100%', p: 0 }}>
                    <WidgetHost
                      type="priceChart"
                      config={{ profileId: profile.id, ticker: effectiveTicker }}
                      inputs={{ ticker: effectiveTicker }}
                      disableCard
                      contentSx={{ p: 2, height: 400 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ height: '100%', p: 0 }}>
                    <WidgetHost
                      type="keyStats"
                      config={{ profileId: profile.id, ticker: effectiveTicker }}
                      inputs={{ ticker: effectiveTicker }}
                      disableCard
                      contentSx={{ p: 2, height: 400 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent sx={{ p: 0 }}>
                    <WidgetHost
                      type="keyRatios"
                      config={{ profileId: profile.id, ticker: effectiveTicker }}
                      inputs={{ ticker: effectiveTicker }}
                      disableCard
                      contentSx={{ p: 2 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent sx={{ p: 0 }}>
                    <WidgetHost
                      type="financialStatements"
                      config={{ profileId: profile.id }}
                      disableCard
                      contentSx={{ p: 2 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    borderRadius: 2,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    p: 3,
                    bgcolor: 'background.paper',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                  }}
                >
                  <Typography variant="h6" fontWeight="600">Latest News</Typography>
                  <Grid container spacing={2}>
                    {(marketNews ?? []).slice(0, NEWS_DISPLAY_LIMIT).map((article, idx) => {
                      const key = `${article.url || article.title}-${idx}`;
                      const cardBody = (
                        <Stack spacing={1.5} sx={{ p: 2.5, height: '100%' }}>
                          <Typography variant="subtitle1" fontWeight="600" lineHeight={1.3}>
                            {article.title}
                          </Typography>
                          {article.content && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                flex: 1,
                              }}
                            >
                              {article.content}
                            </Typography>
                          )}
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 'auto' }}>
                            {article.source && (
                              <Chip 
                                label={article.source} 
                                size="small" 
                                variant="outlined" 
                                sx={{ height: 20, fontSize: '0.7rem' }} 
                              />
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {formatRelativeTime(article.publishedAt)}
                            </Typography>
                          </Stack>
                        </Stack>
                      );

                      return (
                        <Grid item xs={12} md={6} lg={4} key={key}>
                          <Card 
                            variant="outlined" 
                            sx={{ 
                              height: '100%', 
                              borderRadius: 2,
                              transition: 'transform 0.2s, box-shadow 0.2s',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: 2,
                              }
                            }}
                          >
                            {article.url ? (
                              <CardActionArea
                                component="a"
                                href={article.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ height: '100%', alignItems: 'stretch', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}
                              >
                                <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>{cardBody}</CardContent>
                              </CardActionArea>
                            ) : (
                              <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>{cardBody}</CardContent>
                            )}
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                    {newsError && (
                      <Alert severity="error">
                        {newsError}
                      </Alert>
                    )}
                    {!newsError && (marketNews?.length ?? 0) === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No recent articles available.
                      </Typography>
                    )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <WidgetHost type="calendar" config={{ profileId: profile.id }} disableCard contentSx={{ p: 2 }} />
              </Grid>
              {/* Exposures & Filings panel */}
              <Grid item xs={12}>
                <ProfileExposurePanel
                  profileId={profile.id}
                  ticker={effectiveTicker}
                  instrumentId={profile.instrumentId || null}
                />
              </Grid>
            </Grid>
          )}
        </Stack>

      </Container>
    </Box>
  );
};
